import type {
    ContentDefinition,
    ForBlock,
    PluginDefinition,
} from "@magic-md/core";
import type {
    ContentExecutionEntry,
    ExecutionEntry,
    ForExecutionEntry,
    ImportExecutionEntry,
    RunAllProgress,
    UseExecutionEntry,
} from "./execution-state";

export type RunAllExecutionProjection = RunAllProgress;

/**
 * Canonical client-side projection for one Engine session.
 *
 * The Engine remains authoritative. This object only contains the reactive
 * view needed by Editor/Web and can be rebuilt from session state and events.
 */
export interface SessionExecutionSnapshot {
    executions: Map<string, ExecutionEntry>;
    runAllState: {
        isRunning: boolean;
        progress: RunAllExecutionProjection | null;
    } | null;
    activeRunAllExecutionId: string | null;
    activeExecutionIdsByUnit: Map<string, string>;
    engineDefinitions: Map<string, PluginDefinition>;
    engineContentDefinitions: Map<string, ContentDefinition>;
    engineForBlocks: Map<string, ForBlock>;
    forExecutions: Map<string, ForExecutionEntry>;
    contentExecutions: Map<string, ContentExecutionEntry>;
    importExecutions: Map<string, ImportExecutionEntry>;
    useExecutions: Map<string, UseExecutionEntry>;
}

export interface ActiveExecutionProjection {
    executions: Map<string, ExecutionEntry>;
    isRunningAll: boolean;
    runAllProgress: RunAllExecutionProjection | null;
    activeRunAllExecutionId: string | null;
    activeExecutionIdsByUnit: Map<string, string>;
    engineDefinitions: Map<string, PluginDefinition>;
    engineContentDefinitions: Map<string, ContentDefinition>;
    engineForBlocks: Map<string, ForBlock>;
    forExecutions: Map<string, ForExecutionEntry>;
    contentExecutions: Map<string, ContentExecutionEntry>;
    importExecutions: Map<string, ImportExecutionEntry>;
    useExecutions: Map<string, UseExecutionEntry>;
}

export function createEmptySessionExecutionSnapshot(): SessionExecutionSnapshot {
    return {
        executions: new Map(),
        runAllState: null,
        activeRunAllExecutionId: null,
        activeExecutionIdsByUnit: new Map(),
        engineDefinitions: new Map(),
        engineContentDefinitions: new Map(),
        engineForBlocks: new Map(),
        forExecutions: new Map(),
        contentExecutions: new Map(),
        importExecutions: new Map(),
        useExecutions: new Map(),
    };
}

/**
 * Return an independently mutable snapshot. Event reducers use copy-on-write
 * helpers when they only touch one unit category; callers that may touch every
 * category use this full clone.
 */
export function getOrCreateSessionExecutionSnapshot(
    sessionsById: ReadonlyMap<string, SessionExecutionSnapshot>,
    sessionId: string,
): SessionExecutionSnapshot {
    const existing = sessionsById.get(sessionId);
    if (!existing) return createEmptySessionExecutionSnapshot();
    return {
        executions: new Map(existing.executions),
        runAllState: existing.runAllState ? { ...existing.runAllState } : null,
        activeRunAllExecutionId: existing.activeRunAllExecutionId,
        activeExecutionIdsByUnit: new Map(existing.activeExecutionIdsByUnit),
        engineDefinitions: new Map(existing.engineDefinitions),
        engineContentDefinitions: new Map(existing.engineContentDefinitions),
        engineForBlocks: new Map(existing.engineForBlocks),
        forExecutions: new Map(existing.forExecutions),
        contentExecutions: new Map(existing.contentExecutions),
        importExecutions: new Map(existing.importExecutions),
        useExecutions: new Map(existing.useExecutions),
    };
}

export function snapshotActiveExecutionProjection(
    projection: ActiveExecutionProjection,
): SessionExecutionSnapshot {
    return {
        executions: new Map(projection.executions),
        runAllState:
            projection.isRunningAll || projection.runAllProgress
                ? {
                      isRunning: projection.isRunningAll,
                      progress: projection.runAllProgress,
                  }
                : null,
        activeRunAllExecutionId: projection.activeRunAllExecutionId,
        activeExecutionIdsByUnit: new Map(projection.activeExecutionIdsByUnit),
        engineDefinitions: new Map(projection.engineDefinitions),
        engineContentDefinitions: new Map(projection.engineContentDefinitions),
        engineForBlocks: new Map(projection.engineForBlocks),
        forExecutions: new Map(projection.forExecutions),
        contentExecutions: new Map(projection.contentExecutions),
        importExecutions: new Map(projection.importExecutions),
        useExecutions: new Map(projection.useExecutions),
    };
}

export function activateSessionExecutionSnapshot(
    snapshot: SessionExecutionSnapshot,
): ActiveExecutionProjection {
    return {
        executions: new Map(snapshot.executions),
        isRunningAll: snapshot.runAllState?.isRunning ?? false,
        runAllProgress: snapshot.runAllState?.progress ?? null,
        activeRunAllExecutionId: snapshot.activeRunAllExecutionId,
        activeExecutionIdsByUnit: new Map(snapshot.activeExecutionIdsByUnit),
        engineDefinitions: new Map(snapshot.engineDefinitions),
        engineContentDefinitions: new Map(snapshot.engineContentDefinitions),
        engineForBlocks: new Map(snapshot.engineForBlocks),
        forExecutions: new Map(snapshot.forExecutions),
        contentExecutions: new Map(snapshot.contentExecutions),
        importExecutions: new Map(snapshot.importExecutions),
        useExecutions: new Map(snapshot.useExecutions),
    };
}

interface ExecutionUnitMaps {
    executions: Map<string, ExecutionEntry>;
    forExecutions: Map<string, ForExecutionEntry>;
    contentExecutions: Map<string, ContentExecutionEntry>;
    importExecutions: Map<string, ImportExecutionEntry>;
    useExecutions: Map<string, UseExecutionEntry>;
}

export function cloneExecutionMapForUnitType<Projection extends ExecutionUnitMaps>(
    source: Projection,
    unitType: string,
): Projection {
    return {
        ...source,
        executions:
            unitType === "definition"
                ? new Map(source.executions)
                : source.executions,
        forExecutions:
            unitType === "for"
                ? new Map(source.forExecutions)
                : source.forExecutions,
        contentExecutions:
            unitType === "content"
                ? new Map(source.contentExecutions)
                : source.contentExecutions,
        importExecutions:
            unitType === "import"
                ? new Map(source.importExecutions)
                : source.importExecutions,
        useExecutions:
            unitType === "use"
                ? new Map(source.useExecutions)
                : source.useExecutions,
    } as Projection;
}
